import React, { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";
import * as XLSX from "xlsx";

function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(`(max-width:${breakpoint}px)`).matches : false,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width:${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
}

export default function OfficeFilePreview({ file, filename }) {
  const containerRef = useRef(null);
  const [fileType, setFileType] = useState("");
  const [error, setError] = useState("");

  // XLSX state
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState("");
  const [sheetHtml, setSheetHtml] = useState("");
  const [rowLimit, setRowLimit] = useState(200);
  const [totalRows, setTotalRows] = useState(0);

  const isMobile = useIsMobile(600);

  useEffect(() => {
    if (!file) return;

    setError("");
    setSheetHtml("");
    setWorkbook(null);
    setSheetNames([]);
    setActiveSheet("");
    setTotalRows(0);
    if (containerRef.current) containerRef.current.innerHTML = "";

    const name = (filename || (typeof file === "object" && file.name) || "").toLowerCase();
    let type = "unknown";
    if (name.endsWith(".docx")) type = "docx";
    else if (name.endsWith(".xlsx")) type = "xlsx";

    setFileType(type);

    if (!["docx", "xlsx"].includes(type)) {
      setError("Unsupported file format for Office preview.");
      return;
    }

    loadBlob(file, type);
  }, [file, filename]);

  const ensureBlob = async (src) => {
    if (src instanceof Blob) return src;
    if (typeof src === "string") {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`Failed to download (${res.status})`);
      return await res.blob();
    }
    throw new Error("Invalid Office source: neither Blob nor URL.");
  };

  const looksLikeZip = async (blob) => {
    const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
    return head[0] === 0x50 && head[1] === 0x4b; // "PK"
  };

  const loadBlob = async (src, type) => {
    try {
      const blob = await ensureBlob(src);
      if (blob.size === 0) throw new Error("The file is empty or unavailable.");

      if (type === "docx") {
        const zip = await looksLikeZip(blob);
        if (!zip) throw new Error("Downloaded content is not a DOCX (ZIP).");

        await renderAsync(blob, containerRef.current, undefined, {
          inWrapper: true,
          className: "docx",
        });
      } else if (type === "xlsx") {
        const data = await blob.arrayBuffer();
        const wb = XLSX.read(data, { type: "array" });
        setWorkbook(wb);
        setSheetNames(wb.SheetNames || []);
        const first = wb.SheetNames?.[0] || "";
        setActiveSheet(first);
        renderSheetHtml(wb, first, rowLimit);
      }
    } catch (e) {
      setError(e.message || "An unknown error occurred while loading the preview.");
    }
  };

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const renderSheetHtml = (wb, sheetName, limit) => {
    try {
      const ws = wb.Sheets[sheetName];
      if (!ws) {
        setSheetHtml("<div style='padding:8px'>Empty worksheet</div>");
        setTotalRows(0);
        return;
      }

      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
      const total = rows.length;
      setTotalRows(total);

      if (total === 0) {
        setSheetHtml("<div style='padding:8px'>Empty worksheet</div>");
        return;
      }

      // oszlopszám meghatározása a leghosszabb sor alapján
      let maxCols = 0;
      for (const r of rows) maxCols = Math.max(maxCols, r.length);

      // első sor = header
      let [header = [], ...body] = rows;

      // ha üres a header, adjunk automatikus A, B, C... neveket
      const isHeaderEmpty = header.every((v) => v === undefined || v === null || String(v).trim() === "");
      if (isHeaderEmpty) {
        const colLabel = (idx) => {
          let n = idx + 1,
            s = "";
          while (n > 0) {
            const r = (n - 1) % 26;
            s = String.fromCharCode(65 + r) + s;
            n = Math.floor((n - 1) / 26);
          }
          return s;
        };
        header = Array.from({ length: maxCols }, (_, i) => colLabel(i));
      } else if (header.length < maxCols) {
        // rövid header kipótlása
        header = [...header, ...Array(maxCols - header.length).fill("")];
      }

      const cutBody = typeof limit === "number" ? body.slice(0, limit) : body;

      // HTML generálás (fix layout a szebb tördeléshez)
      let html = `<div class="xlsx-preview-inner"><table class="xlsx-table"><thead><tr>`;
      for (let i = 0; i < header.length; i++) {
        html += `<th>${escapeHtml(header[i])}</th>`;
      }
      html += `</tr></thead><tbody>`;

      for (let r = 0; r < cutBody.length; r++) {
        html += `<tr>`;
        const row = cutBody[r] || [];
        for (let c = 0; c < header.length; c++) {
          html += `<td>${escapeHtml(row[c])}</td>`;
        }
        html += `</tr>`;
      }
      html += `</tbody></table></div>`;

      setSheetHtml(html);
    } catch (e) {
      setError("Failed to render XLSX worksheet.");
    }
  };

  useEffect(() => {
    if (workbook && activeSheet) {
      renderSheetHtml(workbook, activeSheet, rowLimit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheet, rowLimit]);

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      {error && <p style={{ color: "red" }}>{error}</p>}

      {fileType === "docx" && !error && (
        <div
          ref={containerRef}
          className="docx-view"
          style={{
            maxWidth: isMobile ? "100%!important" : "900px!important",
            margin: "0 auto!important",
            padding: isMobile ? "0!important" : "12px!important",
            background: "#fff",
            color: "#111",
          }}
        />
      )}

      {fileType === "xlsx" && !error && (
        <div>
          {sheetNames.length > 1 && (
            <div className="sheet-tabs" role="tablist" aria-label="Sheets">
              {sheetNames.map((name) => (
                <button
                  key={name}
                  role="tab"
                  aria-selected={activeSheet === name}
                  className={`sheet-tab${activeSheet === name ? " active" : ""}`}
                  onClick={() => setActiveSheet(name)}
                  title={name}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          <div
            className={`xlsx-preview${isMobile ? " xlsx-mobile" : ""}`}
            dangerouslySetInnerHTML={{ __html: sheetHtml }}
          />

          {fileType === "xlsx" && typeof totalRows === "number" && totalRows > rowLimit && (
            <div className="xlsx-actions">
              <button className="xlsx-btn" onClick={() => setRowLimit(undefined)}>
                Show all rows ({totalRows})
              </button>
              {rowLimit !== 200 && (
                <button className="xlsx-btn secondary" onClick={() => setRowLimit(200)}>
                  Back to 200 rows
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
